import React, { useState } from 'react';

// TenderTiles — the shared Checkout Core collect primitive.
//
// Renders the four payment tiles (Tap/Swipe · Pay Online · Cash · Check) plus an
// opt-in "Split / take a partial amount" affordance. It is PRESENTATIONAL: it
// owns only local input state (which tile is mid-confirm, split mode, the amount
// and check-number fields) and delegates the actual charge to `onCollect`. The
// container does materialize-last, the record-partial vs full routing, the
// pre-paid freshness re-check, and drives `processing`/`error` back in.
//
// This replaces the FINISHED-state payment-method dropdown + "Process Payment" +
// the centered SplitPaymentModal in requestimateState.jsx, and is reused as-is by
// the standalone register and the checkout drawer.
//
// Props:
//   amountDue          number  — remaining balance in dollars
//   collectedPayments  array   — optional; when non-empty, shows partial progress
//   processing         bool    — disables tiles + shows working state
//   error              string  — optional error to surface
//   allowPartial       bool    — show the split affordance (default true)
//   onCollect({ method, amount, checkNumber })
//                      method: 'terminal' | 'card' | 'cash' | 'check'
//                      amount: number (dollars) for a partial, or null for full remaining
//                      checkNumber: string | null

const TEAL = '#3FB2BE'; // theme-ignore-line (concatenated with alpha suffix)

const fmt = (n) => `$${(parseFloat(n) || 0).toFixed(2)}`;

const TILES = [
  { method: 'terminal', label: 'Tap / Swipe', icon: 'fa-credit-card', confirm: false },
  { method: 'card', label: 'Pay Online', icon: 'fa-link', confirm: false },
  { method: 'cash', label: 'Cash', icon: 'fa-money-bill-1', confirm: true },
  { method: 'check', label: 'Check', icon: 'fa-money-check-pen', confirm: true },
];

const TenderTiles = ({
  amountDue,
  collectedPayments = [],
  processing = false,
  error = null,
  allowPartial = true,
  onCollect,
  onCancel = null,
  workingLabel = null,
}) => {
  const [confirmMethod, setConfirmMethod] = useState(null); // 'cash' | 'check' | null
  const [splitMode, setSplitMode] = useState(false);
  const [amountInput, setAmountInput] = useState('');
  const [checkNumber, setCheckNumber] = useState('');
  const [localError, setLocalError] = useState(null);

  const hasPartials = collectedPayments.length > 0;

  // Full Pay Online opens the MAIN invoice's hosted page, whose total split legs
  // (record-partial) never reduce — after a leg it would ask for the whole invoice
  // again. Hidden once part is paid, as on mobile. Split → amount → Pay Online
  // stays: it raises a separate invoice for just that amount. A paid deposit is
  // not a leg (it's netted into the invoice at materialize), so it doesn't hide it.
  const hideFullPayOnline = hasPartials && !splitMode;
  const tiles = hideFullPayOnline ? TILES.filter((t) => t.method !== 'card') : TILES;

  // Pure (no side effects) — the split amount for display, or null (= full balance).
  const partialAmount = () => {
    if (!splitMode || !amountInput) return null;
    const n = parseFloat(amountInput);
    return Number.isNaN(n) ? null : n;
  };

  const fire = (method, checkNo = null) => {
    setLocalError(null);
    let amount = null;
    if (splitMode && amountInput) {
      const n = parseFloat(amountInput);
      if (Number.isNaN(n) || n <= 0) {
        setLocalError('Enter an amount greater than zero.');
        return;
      }
      if (n > amountDue + 0.001) {
        setLocalError(`Amount can't exceed the ${fmt(amountDue)} balance.`);
        return;
      }
      amount = n;
    }
    onCollect && onCollect({ method, amount, checkNumber: checkNo });
  };

  const handleTile = (tile) => {
    if (processing) return;
    setLocalError(null);
    if (tile.confirm) {
      setConfirmMethod(tile.method);
    } else {
      fire(tile.method);
    }
  };

  const shownError = localError || error;

  return (
    <div className="tender-tiles">
      <div className="text-center mb-4">
        {hasPartials && (
          <div className="text-muted" style={{ fontSize: 12 }}>
            {collectedPayments.length} payment{collectedPayments.length === 1 ? '' : 's'} collected · balance due
          </div>
        )}
        <div className="d-flex align-items-baseline justify-content-center" style={{ gap: 8 }}>
          <span style={{ fontSize: 16, fontWeight: 600, color: 'var(--color-text-secondary)' }}>Total :</span>
          <span style={{ fontSize: 28, fontWeight: 700, lineHeight: 1.2 }}>{fmt(amountDue)}</span>
        </div>
      </div>

      {shownError && (
        <div className="alert alert-danger py-2 my-2" style={{ fontSize: 13 }} role="alert">
          {shownError}
        </div>
      )}

      {processing && (
        <div className="text-center my-3">
          <div className="text-muted" style={{ fontSize: 13 }}>
            <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true" />
            {workingLabel || 'Working…'}
          </div>
          {onCancel && (
            <button
              type="button"
              className="btn btn-outline-danger btn-sm mt-3"
              onClick={onCancel}
            >
              <i className="fa-sharp fa-regular fa-xmark mr-2" />
              Cancel
            </button>
          )}
        </div>
      )}

      {/* Cash confirm */}
      {!processing && confirmMethod === 'cash' && (
        <div className="text-center my-2">
          <div className="mb-3" style={{ fontSize: 15 }}>
            Confirm <strong>{fmt(partialAmount() ?? amountDue)}</strong> received in cash?
          </div>
          <button className="jv-btn jv-btn-primary" style={{ background: 'var(--color-brand-teal)', borderColor: 'var(--color-brand-teal)' }} onClick={() => fire('cash')}>
            <i className="fa-sharp fa-regular fa-check mr-2" /> Mark paid
          </button>
          <button className="btn btn-link btn-sm d-block mx-auto mt-2" onClick={() => setConfirmMethod(null)}>← Back</button>
        </div>
      )}

      {/* Check confirm + number */}
      {!processing && confirmMethod === 'check' && (
        <div className="my-2">
          <div className="text-center mb-2" style={{ fontSize: 15 }}>
            Confirm <strong>{fmt(partialAmount() ?? amountDue)}</strong> received by check
          </div>
          <div className="form-group">
            <label htmlFor="tt-check" style={{ fontSize: 13 }} className="font-weight-bold">Check number</label>
            <input
              id="tt-check"
              type="text"
              className="form-control"
              value={checkNumber}
              onChange={(e) => setCheckNumber(e.target.value)}
              placeholder="e.g. 1024"
              autoFocus
            />
          </div>
          <button
            className="jv-btn jv-btn-primary btn-block"
            style={{ background: 'var(--color-brand-teal)', borderColor: 'var(--color-brand-teal)' }}
            onClick={() => fire('check', (checkNumber || '').trim() || null)}
          >
            <i className="fa-sharp fa-regular fa-check mr-2" /> Mark paid
          </button>
          <button className="btn btn-link btn-sm d-block mx-auto mt-2" onClick={() => setConfirmMethod(null)}>← Back</button>
        </div>
      )}

      {/* Tiles + split */}
      {!processing && confirmMethod === null && (
        <>
          {splitMode && (
            <div className="form-group mb-2">
              <label htmlFor="tt-amount" style={{ fontSize: 13 }} className="font-weight-bold">
                Amount to collect now
              </label>
              <div className="input-group">
                <div className="input-group-prepend"><span className="input-group-text">$</span></div>
                <input
                  id="tt-amount"
                  type="number"
                  min="0"
                  step="0.01"
                  className="form-control"
                  value={amountInput}
                  onChange={(e) => setAmountInput(e.target.value)}
                  placeholder={amountDue?.toFixed ? amountDue.toFixed(2) : amountDue}
                  autoFocus
                />
              </div>
              <small className="form-text text-muted">Balance: {fmt(amountDue)}. Then pick a method below.</small>
            </div>
          )}

          <div className={`tender-tiles-grid${tiles.length === 3 ? ' tender-tiles-grid--3' : ''}`}>
            {tiles.map((tile) => (
              <button
                key={tile.method}
                type="button"
                className="tender-tile"
                onClick={() => handleTile(tile)}
                disabled={processing}
              >
                <i className={`fa-sharp fa-regular ${tile.icon}`} style={{ color: 'var(--color-brand-teal)', fontSize: 20 }} />
                <span style={{ fontSize: 12, fontWeight: 500 }}>{tile.label}</span>
              </button>
            ))}
          </div>

          {hideFullPayOnline && (
            <small className="d-block text-muted text-center mt-2">
              Pay Online collects the whole invoice, so it isn't available once part of it is paid.
            </small>
          )}

          {allowPartial && (
            <button
              type="button"
              className="btn btn-light btn-block mt-2 d-flex align-items-center justify-content-center"
              style={{ fontSize: 14, fontWeight: 500 }}
              onClick={() => { setSplitMode((s) => !s); setAmountInput(''); setLocalError(null); }}
            >
              <i className="fa-sharp fa-regular fa-down-left-and-up-right-to-center mr-2" />
              {splitMode ? 'Collect full balance instead' : 'Split / take a partial amount'}
            </button>
          )}
        </>
      )}

      <style>{`
        .tender-tiles-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 8px;
        }
        .tender-tiles-grid--3 { grid-template-columns: repeat(3, 1fr); }
        .tender-tile {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 14px 6px;
          background: var(--color-bg-card);
          border: 1px solid var(--color-border);
          border-radius: 6px;
          cursor: pointer;
          transition: border-color .12s, box-shadow .12s;
        }
        .tender-tile:hover:not(:disabled) { border-color: ${TEAL}; box-shadow: 0 0 0 1px ${TEAL}33; }
        .tender-tile:disabled { opacity: .5; cursor: not-allowed; }
        @media (max-width: 520px) {
          .tender-tiles-grid { grid-template-columns: repeat(2, 1fr); }
        }
      `}</style>
    </div>
  );
};

export default TenderTiles;
